import { API_BASE_URL } from '@/config/env.js'

const ACCESS_KEY = 'vw_access'
const REFRESH_KEY = 'vw_refresh'
let sessionVersion = 0
let authAttempt = 0
let refreshRequest = null
const sessionListeners = new Set()

function sessionChanged() {
  sessionVersion += 1
  sessionListeners.forEach(listener => listener())
}

function assertCurrentSession(session) {
  if (!authStore.isCurrentSession(session)) {
    throw new DOMException('A sessão mudou. A solicitação anterior foi descartada.', 'AbortError')
  }
}

function writeTokens(access, refresh) {
  sessionStorage.setItem(ACCESS_KEY, access)
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh)
  else localStorage.removeItem(REFRESH_KEY)
}

export const authStore = {
  getAccess: () => sessionStorage.getItem(ACCESS_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  getSession: () => sessionVersion,
  isCurrentSession: session => session === sessionVersion,
  subscribe: listener => {
    sessionListeners.add(listener)
    return () => sessionListeners.delete(listener)
  },
  setTokens: (access, refresh) => {
    writeTokens(access, refresh)
    sessionChanged()
  },
  clear: () => {
    sessionStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
    sessionChanged()
  },
}

export function refreshAccessToken() {
  const session = authStore.getSession()
  if (refreshRequest?.session === session) return refreshRequest.promise
  const refresh = authStore.getRefresh()
  if (!refresh) return Promise.resolve(null)
  const request = { session }
  request.promise = renewSession(session, refresh).finally(() => {
    if (refreshRequest === request) refreshRequest = null
  })
  refreshRequest = request
  return request.promise
}

async function renewSession(session, refresh) {
  try {
    const res = await fetch(`${API_BASE_URL}/accounts/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    })
    if (!authStore.isCurrentSession(session)) return null
    if (!res.ok) { authStore.clear(); return null }
    const data = await res.json()
    if (!authStore.isCurrentSession(session)) return null
    // Token rotation belongs to the existing session, unlike login/logout.
    writeTokens(data.access, data.refresh || refresh)
    return data.access
  } catch (error) {
    if (!authStore.isCurrentSession(session)) return null
    throw error
  }
}

async function authenticate(path, payload, errorMessage) {
  const session = authStore.getSession()
  const attempt = ++authAttempt
  const assertCurrentAttempt = () => {
    assertCurrentSession(session)
    if (attempt !== authAttempt) {
      throw new DOMException('Uma nova tentativa de acesso substituiu a anterior.', 'AbortError')
    }
  }
  let res
  let data
  try {
    res = await fetch(`${API_BASE_URL}/accounts/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    assertCurrentAttempt()
    data = res.ok ? await res.json() : await res.json().catch(() => ({}))
    assertCurrentAttempt()
  } catch (error) {
    assertCurrentAttempt()
    throw error
  }
  if (!res.ok) throw new Error(errorMessage(data))
  authStore.setTokens(data.access, data.refresh)
  return data
}

export async function login(email, password) {
  return authenticate('token/', { username: email.trim().toLowerCase(), password },
    error => error?.detail || 'Credenciais inválidas')
}

async function passwordResetRequest(path, payload, { signal } = {}) {
  const controller = new AbortController()
  const cancel = () => controller.abort()
  if (signal?.aborted) cancel()
  else signal?.addEventListener('abort', cancel, { once: true })
  let timedOut = false
  const timeout = setTimeout(() => { timedOut = true; cancel() }, 20000)
  let res
  let data
  try {
    res = await fetch(`${API_BASE_URL}/accounts/password/reset/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
      cache: 'no-store',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
    })
    data = await res.json().catch(() => null)
  } catch (error) {
    if (signal?.aborted) throw error
    if (timedOut) throw new Error('A solicitação demorou mais que o esperado. Tente novamente em instantes.')
    throw new Error('Não foi possível conectar. Verifique sua conexão e tente novamente.')
  } finally {
    clearTimeout(timeout)
    signal?.removeEventListener('abort', cancel)
  }
  if (timedOut) throw new Error('A solicitação demorou mais que o esperado. Tente novamente em instantes.')
  if (res.ok && (!data || (path === 'validate/' ? data.valid !== true : typeof data.detail !== 'string'))) {
    throw new Error('O servidor enviou uma resposta inesperada. Tente novamente em instantes.')
  }
  data = data && typeof data === 'object' ? data : {}
  if (!res.ok) {
    const retryAfter = Number(res.headers.get('Retry-After')) || 60
    const message = res.status === 429
      ? `Muitas tentativas. Aguarde ${Math.ceil(retryAfter / 60)} minuto(s) e tente novamente.`
      : res.status >= 500
        ? 'Não foi possível concluir agora. Tente novamente em instantes.'
        : data.detail || Object.entries(data).filter(([key]) => key !== 'code').flatMap(([, value]) => value).join(' ') || 'Não foi possível concluir a solicitação.'
    const error = new Error(message)
    error.status = res.status
    error.code = data.code
    error.data = data
    error.retryAfter = retryAfter
    throw error
  }
  return data
}

export function requestPasswordReset(email) {
  return passwordResetRequest('', { email: email.trim().toLowerCase() })
}

export function validatePasswordReset(credentials, options) {
  return passwordResetRequest('validate/', credentials, options)
}

export function confirmPasswordReset(credentials) {
  return passwordResetRequest('confirm/', credentials)
}

export async function register(input, password, displayName, acceptedTerms) {
  const payload = typeof input === 'object' && input !== null
    ? input
    : { email: input, password, displayName, acceptedTerms }
  return authenticate('register/', {
      email: payload.email,
      password: payload.password,
      display_name: payload.displayName,
      phone_number: payload.phoneNumber,
      accepted_terms: payload.acceptedTerms,
      date_of_birth: payload.dateOfBirth,
      guardian_email: payload.guardianEmail,
      guardian_consent: !!payload.guardianConsent,
    }, error => Object.values(error).flat().join(' ') || 'Erro no cadastro')
}

export async function fetchCurrentLegalDocs() {
  const res = await fetch(`${API_BASE_URL}/accounts/legal/current/`)
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err?.detail || 'Não foi possível carregar os documentos legais')
  }
  return res.json()
}

export async function updateMyProfile(patch) {
  const session = authStore.getSession()
  const res = await apiFetch(`${API_BASE_URL}/accounts/me/`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    assertCurrentSession(session)
    const msg = Object.values(err).flat().join(' ') || 'Não foi possível salvar'
    const error = new Error(msg)
    error.status = res.status
    error.data = err
    throw error
  }
  const data = await res.json()
  assertCurrentSession(session)
  return data
}

export function logout() {
  authStore.clear()
}

export async function apiFetch(url, options = {}) {
  const session = authStore.getSession()
  let access = authStore.getAccess()
  const headers = { 'Content-Type': 'application/json', ...options.headers }
  if (access) headers['Authorization'] = `Bearer ${access}`

  try {
    let res = await fetch(url, { ...options, headers })
    assertCurrentSession(session)
    if (res.status === 401 && authStore.getRefresh()) {
      access = await refreshAccessToken()
      assertCurrentSession(session)
      if (access) {
        res = await fetch(url, { ...options, headers: { ...headers, Authorization: `Bearer ${access}` } })
        assertCurrentSession(session)
      }
    }
    return res
  } catch (error) {
    assertCurrentSession(session)
    throw error
  }
}
